import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import background from "./images/background.png";

const API = process.env.REACT_APP_API_URL || "";
const TOKEN_KEY = "kp_admin_token";

function TagChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="py-1.5 px-3 rounded-full text-xs font-bold whitespace-nowrap active:scale-95 transition-transform"
      style={
        active
          ? { background: "#b06ab3", color: "white", border: "1.5px solid #b06ab3" }
          : {
              background: "white",
              color: "#b06ab3",
              border: "1.5px solid rgba(176,106,179,0.3)",
            }
      }
    >
      {label}
    </button>
  );
}

function VideoAdminCard({ video, tags, onUpdate }) {
  const [saving, setSaving] = useState(false);

  const toggleTag = (slug) => {
    const next = video.tagSlugs.includes(slug)
      ? video.tagSlugs.filter((s) => s !== slug)
      : [...video.tagSlugs, slug];
    onUpdate(video.id, { tagSlugs: next }, setSaving);
  };

  const toggleVisible = () => {
    onUpdate(video.id, { isVisible: !video.is_visible }, setSaving);
  };

  return (
    <div
      className="p-3 flex gap-3"
      style={{
        background: "rgba(255,255,255,0.9)",
        borderRadius: "20px",
        boxShadow: "0 4px 24px rgba(176,106,179,0.13)",
        border: "1.5px solid rgba(176,106,179,0.2)",
        opacity: video.is_visible ? 1 : 0.55,
      }}
    >
      <img
        src={video.thumbnail_url}
        alt=""
        style={{
          width: "72px",
          height: "90px",
          objectFit: "cover",
          borderRadius: "12px",
          flexShrink: 0,
        }}
      />
      <div className="flex-1 min-w-0">
        <p
          className="text-xs font-semibold mb-2 line-clamp-2"
          style={{ color: "#2d1f3d" }}
        >
          {video.caption || "(sin caption)"}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {tags.map((tag) => (
            <TagChip
              key={tag.slug}
              label={tag.label}
              active={video.tagSlugs.includes(tag.slug)}
              onClick={() => toggleTag(tag.slug)}
            />
          ))}
        </div>
        <div className="flex items-center justify-between">
          <button
            onClick={toggleVisible}
            className="text-xs font-bold underline"
            style={{ color: video.is_visible ? "#9b7eaa" : "#b06ab3" }}
          >
            {video.is_visible ? "👁️ Visible — ocultar" : "🙈 Oculto — mostrar"}
          </button>
          {saving && (
            <span className="text-[10px]" style={{ color: "#9b7eaa" }}>
              guardando…
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminVideoTags() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [tokenError, setTokenError] = useState("");
  const [videos, setVideos] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = async (activeToken) => {
    setLoading(true);
    setError(null);
    try {
      const [videosRes, tagsRes] = await Promise.all([
        fetch(`${API}/api/admin/video-moments`, {
          headers: { "x-admin-token": activeToken },
        }),
        fetch(`${API}/api/tags`),
      ]);

      if (videosRes.status === 403) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setTokenError("Token incorrecto bb, intenta otra vez");
        return;
      }
      if (!videosRes.ok || !tagsRes.ok) throw new Error("bad response");

      setVideos(await videosRes.json());
      setTags(await tagsRes.json());
    } catch (err) {
      setError("No se pudieron cargar los videos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadData(token);
  }, [token]);

  const handleTokenSubmit = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    setTokenError("");
    localStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
  };

  const updateVideo = async (id, patch, setSaving) => {
    const previous = videos;
    setVideos((current) =>
      current.map((v) =>
        v.id === id
          ? {
              ...v,
              ...(patch.tagSlugs ? { tagSlugs: patch.tagSlugs } : {}),
              ...(typeof patch.isVisible === "boolean"
                ? { is_visible: patch.isVisible }
                : {}),
            }
          : v
      )
    );
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/video-moments/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-admin-token": token,
        },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error("bad response");
    } catch (err) {
      setVideos(previous);
      toast("No se pudo guardar, intenta de nuevo", { autoClose: 2000 });
    } finally {
      setSaving(false);
    }
  };

  if (!token) {
    return (
      <div
        className="flex flex-col items-center justify-center px-6 font-quicksand"
        style={{
          backgroundImage: `url(${background})`,
          backgroundSize: "cover",
          minHeight: "calc(100vh - 120px)",
        }}
      >
        <form
          onSubmit={handleTokenSubmit}
          className="w-full max-w-xs p-6"
          style={{
            background: "rgba(255,255,255,0.92)",
            borderRadius: "24px",
            boxShadow: "0 4px 24px rgba(176,106,179,0.2)",
          }}
        >
          <h1
            className="text-lg font-extrabold mb-4 text-center"
            style={{ color: "#2d1f3d" }}
          >
            Solo para ti, bb 🔒
          </h1>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin token"
            className="w-full p-3 rounded-2xl text-sm mb-3"
            style={{ border: "1.5px solid rgba(176,106,179,0.3)" }}
          />
          {tokenError && (
            <p
              className="text-xs mb-3 text-center"
              style={{ color: "#c0392b" }}
            >
              {tokenError}
            </p>
          )}
          <button
            type="submit"
            className="w-full py-3 rounded-2xl text-sm font-bold"
            style={{ background: "#b06ab3", color: "white" }}
          >
            Entrar
          </button>
        </form>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col font-quicksand"
      style={{
        backgroundImage: `url(${background})`,
        backgroundRepeat: "repeat-y",
        backgroundSize: "100% auto",
        backgroundPosition: "top center",
        minHeight: "calc(100vh - 120px)",
      }}
    >
      <div
        className="px-5 pt-6 pb-8 flex flex-col"
        style={{ minHeight: "inherit" }}
      >
        <h1 className="text-xl font-extrabold mb-4" style={{ color: "#2d1f3d" }}>
          Tags de videos
        </h1>

        {loading && (
          <div className="flex justify-center py-8">
            <div
              className="animate-spin rounded-full h-7 w-7 border-2"
              style={{ borderColor: "#b06ab3", borderTopColor: "transparent" }}
            />
          </div>
        )}

        {error && (
          <p className="text-center py-8 text-sm" style={{ color: "#c0392b" }}>
            {error}
          </p>
        )}

        <div className="flex flex-col gap-3">
          {videos.map((video) => (
            <VideoAdminCard
              key={video.id}
              video={video}
              tags={tags}
              onUpdate={updateVideo}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
